import React, { useCallback, useEffect, ChangeEvent, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/redux/store';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Box,
    Flex,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
    Table,
    Tbody,
    Td,
    Thead,
    Tr,
    Spinner as SpinnerChakra,
    Stack,
    Switch,
    Text,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { HiX } from 'react-icons/hi';
import * as yup from 'yup';
import { debounce } from '@/utils/helpers';
import { Pagination } from '@/components/Pagination';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import {
    getAllCustomerQuizzesV2,
    resetQuizCustomerV2Pages,
    selectQuizCustomerV2ActivePage,
    selectQuizCustomerV2Loading,
    selectQuizCustomerV2Page,
    selectQuizCustomerV2TotalPages,
    setQuizCustomerV2ActivePage,
} from '@/redux/reducers/quizzes/quizCustomerV2';
import { CustomerQuizItemV2 } from '@/features/Quiz/components/CustomerQuizV2/CustomerQuizItemV2';
import { toCustomerQuizItemDto } from '@/features/Quiz/types';

const searchInputSchema = yup.object().shape({
    keyword: yup.string(),
});

type SearchFormData = {
    keyword: string;
};

export const CustomerQuizV2 = () => {
    const dispatch = useDispatch<AppDispatch>();

    const activePage = useSelector(selectQuizCustomerV2ActivePage, shallowEqual);
    const totalPages = useSelector(selectQuizCustomerV2TotalPages, shallowEqual);
    const quizzes = useSelector((state: RootState) => selectQuizCustomerV2Page(state, activePage), shallowEqual);
    const isLoading = useSelector(selectQuizCustomerV2Loading, shallowEqual);

    const [isShowExpired, setIsShowExpired] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { isSubmitting },
        watch,
        getValues,
        setValue,
    } = useForm<SearchFormData>({
        resolver: yupResolver(searchInputSchema),
        defaultValues: {
            keyword: '',
        },
    });

    const onSearchKeywordSubmit = useCallback(
        (formValues: SearchFormData) => {
            dispatch(resetQuizCustomerV2Pages());
            dispatch(setQuizCustomerV2ActivePage(1));

            dispatch(
                getAllCustomerQuizzesV2({
                    activePage: 1,
                    activeSearchKeyword: formValues.keyword,
                    isShowExpired,
                })
            );
        },
        [dispatch, isShowExpired]
    );

    const onSearchKeywordChange = debounce(
        useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();

                setValue('keyword', e.target.value);

                dispatch(resetQuizCustomerV2Pages());
                dispatch(setQuizCustomerV2ActivePage(1));

                dispatch(
                    getAllCustomerQuizzesV2({
                        activePage: 1,
                        activeSearchKeyword: e.target.value ?? '',
                        isShowExpired,
                    })
                );
            },
            [dispatch, isShowExpired, setValue]
        ),
        300
    );

    const handleReset = useCallback(() => {
        reset();

        dispatch(
            getAllCustomerQuizzesV2({
                activePage: 1,
                activeSearchKeyword: '',
                isShowExpired,
            })
        );
    }, [dispatch, reset, isShowExpired]);

    const handlePaginationChange = useCallback(
        (page: number) => {
            dispatch(setQuizCustomerV2ActivePage(page));
        },
        [dispatch]
    );

    useEffect(() => {
        dispatch(
            getAllCustomerQuizzesV2({
                activePage,
                activeSearchKeyword: getValues('keyword') ?? '',
                isShowExpired,
            })
        );
    }, [dispatch, activePage, isShowExpired, getValues]);

    useEffect(
        () => () => {
            dispatch(setQuizCustomerV2ActivePage(1));
        },
        [dispatch]
    );

    const handleToggleHideEnabled = () => {
        setIsShowExpired(!isShowExpired);
    };

    return (
        <Flex w="100%" flexDir="column" mb={'10px'}>
            <Flex gap="20px" sx={{ '.btn-half-width': { flex: '1 0 45%' } }} w="100%">
                <Flex alignItems={'center'} mb={3} w="100%">
                    <form onSubmit={handleSubmit(onSearchKeywordSubmit)}>
                        <InputGroup minW={'400px'} className="input-left-wrap">
                            <InputLeftElement
                                className="InputLeft"
                                pointerEvents={isSubmitting ? 'none' : 'auto'}
                                onClick={handleSubmit(onSearchKeywordSubmit)}>
                                {isSubmitting || isLoading ? <SpinnerChakra size="20px" /> : <FaSearch size="20px" />}
                            </InputLeftElement>
                            <Input
                                placeholder="Search Customer Quizzes by Name"
                                bg={'#ffffff'}
                                id={'keyword-customer-v2'}
                                type={'text'}
                                borderRadius="1000px"
                                {...register('keyword')}
                                onChange={onSearchKeywordChange}
                            />
                            {watch('keyword') && (
                                <InputRightElement cursor="pointer" onClick={handleReset}>
                                    <HiX />
                                </InputRightElement>
                            )}
                        </InputGroup>
                    </form>
                    <Stack ml="20px" alignItems="center" direction="row">
                        <Switch onChange={handleToggleHideEnabled} isChecked={isShowExpired} size="lg" />
                        <Text>Show Expired Quizzes</Text>
                    </Stack>
                </Flex>
            </Flex>
            <Flex flexDir={'column'}>
                <Table bgColor={'#ffffff'}>
                    {quizzes.length > 0 && (
                        <Thead borderBottom="1px solid #e5ebf3">
                            <Tr w="100%">
                                <ThEl align="start" w="65%">
                                    Title
                                </ThEl>
                                <ThEl w="15%">Access</ThEl>
                                <ThEl w="10%">Customer Name</ThEl>

                                <ThEl w="15%">Customer Email</ThEl>
                                <ThEl w="10%">Created Date</ThEl>
                                <ThEl w="15%">Deadline</ThEl>
                                <ThEl w="15%">Actions</ThEl>
                            </Tr>
                        </Thead>
                    )}
                    <Tbody>
                        {quizzes.length > 0 ? (
                            quizzes.map((quiz) => (
                                <CustomerQuizItemV2 key={quiz.id} quiz={toCustomerQuizItemDto(quiz)} />
                            ))
                        ) : (
                            <>
                                {!isLoading && (
                                    <Tr>
                                        <Td>
                                            <Box>No list items</Box>
                                        </Td>
                                    </Tr>
                                )}
                            </>
                        )}
                    </Tbody>
                </Table>

                {totalPages > 1 && (
                    <Flex justifyContent={'end'} mt={'20px'}>
                        <Pagination
                            currentPage={activePage}
                            totalPages={totalPages}
                            handlePagination={handlePaginationChange}
                        />
                    </Flex>
                )}
            </Flex>
        </Flex>
    );
};
